import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { Typography } from '@/components/Typography';
import type {
  ContentfulContactUsFragment,
  ContentfulHeadingFragment,
} from '@/graphql/contentful/api';
import { useContactInfo } from '@/hooks/useContactInfo';
import { serviceMessages } from '@/messages/service';
import { useRegionProvider } from '@/providers/RegionProvider';

import { Heading } from '../Heading';

import { ReactComponent as Mail } from '@/assets/icons/mail-outline-white.svg';
import { ReactComponent as Phone } from '@/assets/icons/phone-outline-white.svg';

import * as S from './ContactUs.styles';

export const ContactUs = ({
  contactHeader,
  email,
  phone,
}: ContentfulContactUsFragment) => {
  const { formatMessage } = useIntl();
  const contactInfo = useContactInfo();
  const { country } = useRegionProvider();

  const contactPhone = phone || contactInfo.phoneNumber;
  const contactEmail = email || contactInfo.email;

  const makePhoneCall = (phoneNumber: string) => {
    window.location.href = 'tel:' + phoneNumber;
  };

  const openEmail = (email: string) => {
    window.location.href = 'mailto:' + email;
  };

  const handleContact = () => {
    if (contactPhone) {
      makePhoneCall(contactPhone);
    } else if (contactEmail) {
      openEmail(contactEmail);
    }
  };
  return (
    <S.Container
      alignItems='center'
      data-test-id='ContactUsContainer'
      justifyContent='center'
    >
      <S.ContentWrapper>
        <S.Content color='lightGrey' data-test-id='ContactUsFlex'>
          {contactHeader && (
            <>
              <Heading
                Heading={contactHeader as ContentfulHeadingFragment}
                data-test-id='ContactUsHeading'
              />
              <Typography
                fontFamily='italianPlate'
                fontWeight='bold'
                marginTop={2}
                scale='h5'
                textAlign='center'
                textTransform='uppercase'
              >
                {country.code} {formatMessage(serviceMessages.contactCenter)}
              </Typography>
            </>
          )}
          <S.ContactData>
            <S.Item
              alignItems='center'
              data-test-id='ContactUsPhoneItem'
              flexDirection='column'
              gridGap={1}
              onClick={() => makePhoneCall(contactPhone)}
            >
              <S.IconWrapper>
                <Phone />
              </S.IconWrapper>
              <Typography
                fontFamily='italianPlate'
                fontWeight='bold'
                scale='h6'
                textAlign='center'
              >
                {contactPhone}
              </Typography>
              <Box>
                <Typography
                  fontFamily='italianPlate'
                  scale='p3'
                  textAlign='center'
                >
                  {contactInfo.weekdayHours}
                </Typography>
                <Typography
                  fontFamily='italianPlate'
                  scale='p3'
                  textAlign='center'
                >
                  {contactInfo.weekendHours}
                </Typography>
              </Box>
            </S.Item>
            <S.Item
              alignItems='center'
              data-test-id='ContactUsEmailItem'
              flexDirection='column'
              gridGap={1}
              onClick={() => openEmail(contactEmail)}
            >
              <S.IconWrapper>
                <Mail />
              </S.IconWrapper>
              <Typography
                fontFamily='italianPlate'
                fontWeight='bold'
                scale='h6'
                textAlign='center'
              >
                {contactEmail}
              </Typography>
              <Box>
                <Typography
                  fontFamily='italianPlate'
                  scale='p3'
                  textAlign='center'
                >
                  {formatMessage(serviceMessages.replyWithinBusinessDayPt1)}
                </Typography>
                <Typography
                  fontFamily='italianPlate'
                  scale='p3'
                  textAlign='center'
                >
                  {formatMessage(serviceMessages.replyWithinBusinessDayPt2)}
                </Typography>
              </Box>
            </S.Item>
          </S.ContactData>

          <S.ButtonContainer
            data-test-id='ContactUsButtonBox'
            onClick={handleContact}
          >
            <Button>{formatMessage(serviceMessages.contactUs)}</Button>
          </S.ButtonContainer>
        </S.Content>
      </S.ContentWrapper>
    </S.Container>
  );
};
